import { useMedicalGroupDetailMutation } from '@pba/react-network';
import { useCallback } from 'react';
import { message } from 'ui';
import type { NodeMembership } from 'models/user';
import { useCreateTelehealthRequestVisitMutation } from '../../../../queries';
import type { TelehealthRequestFormValues } from '../TelehealthRequestContent';
import { useCreateTelehealtVisitData } from './useCreateTelehealtVisitData';

type P = {
  data: TelehealthRequestFormValues;
  onSuccess: () => void;
  userMedicalGroupMemberships?: NodeMembership[];
};

export const useOnFormFinish = () => {
  const { isPending: isRequestVisitLoading, mutate: requestVisit } = useCreateTelehealthRequestVisitMutation();
  const { isPending: isGetMedicalGroupDetailLoading, mutateAsync: getMedicalGroupDetail } =
    useMedicalGroupDetailMutation();
  const createTelehealthVisitData = useCreateTelehealtVisitData();

  const onFormFinish = useCallback(
    async ({ data, onSuccess, userMedicalGroupMemberships }: P) => {
      let medicalGroup = data.medicalGroup;

      if (!medicalGroup) {
        const ref = userMedicalGroupMemberships?.[0]?.ref;

        if (!ref) {
          // This should never happen
          message.error('No medical group found');
          return;
        }

        const medicalGroupDetail = await getMedicalGroupDetail(
          { id: ref },
          {
            onError() {
              message.error(
                'Failed to load medical group details. The telehealth visit request could not be created. Please try again later.',
              );
            },
          },
        );

        medicalGroup = {
          label: medicalGroupDetail.name,
          value: medicalGroupDetail.id,
        };
      }

      requestVisit(
        createTelehealthVisitData({
          data: {
            ...data,
            medicalGroup,
          },
        }),
        {
          onSuccess() {
            onSuccess();
            message.success('Telehealth visit was requested');
          },
        },
      );
    },
    [createTelehealthVisitData, getMedicalGroupDetail, requestVisit],
  );

  return { isLoading: isRequestVisitLoading || isGetMedicalGroupDetailLoading, onFormFinish };
};
